import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarChart3, CalendarDays, MessageSquareText, CheckCircle2, Download, Loader2 } from "lucide-react";

const PRESETS = [
  { days: 7, label: "7 dias" },
  { days: 30, label: "30 dias" },
  { days: 90, label: "90 dias" },
];

function fmtDate(iso) {
  if (!iso) return "";
  return iso.slice(0, 10);
}

export default function SuperadminMetrics() {
  const [period, setPeriod] = useState(30);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [custom, setCustom] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (params) => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/admin/usage-metrics", { params });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Não foi possível carregar as métricas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load({ period: 30 });
  }, [load]);

  const applyPreset = (days) => {
    setCustom(false);
    setPeriod(days);
    load({ period: days });
  };

  const applyCustom = () => {
    if (!start || !end) return;
    setCustom(true);
    load({ start, end });
  };

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Agência", "Eventos criados", "Eventos realizados", "Mensagens enviadas"],
      ...data.by_org.map((o) => [
        o.organization_name, o.events_created, o.events_realized, o.messages_sent,
      ]),
      ["TOTAL", data.totals.events_created, data.totals.events_realized, data.totals.messages_sent],
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `metricas-utilizacao_${fmtDate(data.period.start)}_${fmtDate(data.period.end)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data?.totals || { events_created: 0, events_realized: 0, messages_sent: 0 };
  const cards = [
    { key: "created", label: "Eventos criados", value: totals.events_created, icon: CalendarDays, color: "text-orange-500" },
    { key: "realized", label: "Eventos realizados", value: totals.events_realized, icon: CheckCircle2, color: "text-emerald-500" },
    { key: "messages", label: "Mensagens WhatsApp enviadas", value: totals.messages_sent, icon: MessageSquareText, color: "text-sky-500" },
  ];

  return (
    <div className="space-y-6" data-testid="superadmin-metrics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-orange-500" /> Métricas de utilização
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Utilização por agência — eventos e mensagens enviadas no período.
          </p>
        </div>
        <Button
          onClick={exportCsv}
          disabled={!data || loading}
          data-testid="metrics-export-csv"
          className="gap-2"
        >
          <Download className="w-4 h-4" /> Exportar CSV
        </Button>
      </div>

      {/* Period selector */}
      <div className="flex flex-wrap items-center gap-2" data-testid="metrics-period-selector">
        {PRESETS.map((p) => (
          <Button
            key={p.days}
            variant={!custom && period === p.days ? "default" : "outline"}
            size="sm"
            onClick={() => applyPreset(p.days)}
            data-testid={`metrics-preset-${p.days}`}
          >
            {p.label}
          </Button>
        ))}
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-2">
          <Input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="flex-1 min-w-[130px] sm:w-[150px] sm:flex-none"
            data-testid="metrics-start-date"
          />
          <span className="text-muted-foreground text-sm">até</span>
          <Input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="flex-1 min-w-[130px] sm:w-[150px] sm:flex-none"
            data-testid="metrics-end-date"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={applyCustom}
            disabled={!start || !end}
            data-testid="metrics-apply-custom"
          >
            Aplicar
          </Button>
        </div>
      </div>

      {data?.period && (
        <p className="text-xs text-muted-foreground" data-testid="metrics-period-label">
          Período: {fmtDate(data.period.start)} — {fmtDate(data.period.end)}
        </p>
      )}

      {error && (
        <div className="text-sm text-red-600" data-testid="metrics-error">{error}</div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Card key={c.key} className="p-5" data-testid={`metrics-card-${c.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{c.label}</span>
              <c.icon className={`w-5 h-5 ${c.color}`} />
            </div>
            <div className="text-3xl font-bold mt-2">
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : c.value}
            </div>
          </Card>
        ))}
      </div>

      {/* Per-agency table */}
      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b font-semibold text-sm">Por agência</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="metrics-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b">
                <th className="px-5 py-2 font-medium">Agência</th>
                <th className="px-5 py-2 font-medium text-right">Eventos criados</th>
                <th className="px-5 py-2 font-medium text-right">Eventos realizados</th>
                <th className="px-5 py-2 font-medium text-right">Mensagens enviadas</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_org || []).map((o) => (
                <tr key={o.organization_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`metrics-row-${o.organization_id}`}>
                  <td className="px-5 py-2.5 font-medium">{o.organization_name}</td>
                  <td className="px-5 py-2.5 text-right">{o.events_created}</td>
                  <td className="px-5 py-2.5 text-right">{o.events_realized}</td>
                  <td className="px-5 py-2.5 text-right">{o.messages_sent}</td>
                </tr>
              ))}
              {!loading && (data?.by_org || []).length === 0 && (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-muted-foreground" data-testid="metrics-empty">
                    Sem atividade neste período.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
